/* ═══════════════════════════════════════════════════════════════
   TaskRowMenu — the ONE right-click menu for a List row AND a Board card
   (Decision 5): Open, Move to ▸ (the keyboard path for Board drag),
   Archive/Restore, Delete. Kit ui/context-menu, portal + viewport-clamped
   (DESIGN.md §4), opened at the pointer per the SpecBlockMenu pattern.
   ═══════════════════════════════════════════════════════════════ */
import type { TaskStatus } from '../../../shared/types';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '../../components/ui/context-menu';
import { STATUS_COLS } from './types';

export interface TaskRowMenuPoint {
  x: number;
  y: number;
}

export interface TaskRowMenuProps {
  point: TaskRowMenuPoint;
  currentStatus: TaskStatus;
  isArchived: boolean;
  canEdit: boolean;
  canArchive: boolean;
  canDelete: boolean;
  onClose: () => void;
  onOpen: () => void;
  /** Desktop only: open the task in a second window. Absent on web. */
  onOpenNewWindow?: () => void;
  onMoveTo: (status: TaskStatus) => void;
  onArchiveToggle: () => void;
  onDelete: () => void;
}

export function TaskRowMenu({
  point,
  currentStatus,
  isArchived,
  canEdit,
  canArchive,
  canDelete,
  onClose,
  onOpen,
  onOpenNewWindow,
  onMoveTo,
  onArchiveToggle,
  onDelete,
}: TaskRowMenuProps) {
  return (
    <ContextMenu open onOpenChange={(next) => !next && onClose()}>
      <ContextMenuTrigger asChild>
        <span style={{ position: 'fixed', left: point.x, top: point.y, width: 0, height: 0 }} />
      </ContextMenuTrigger>
      <ContextMenuContent className="min-w-[11.25rem] rounded-md border border-border bg-surface p-1 shadow-lg">
        <ContextMenuItem
          className="cursor-pointer rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-hover"
          onSelect={() => {
            onClose();
            onOpen();
          }}
        >
          Open
        </ContextMenuItem>
        {onOpenNewWindow && (
          <ContextMenuItem
            className="cursor-pointer rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-hover"
            onSelect={() => {
              onClose();
              onOpenNewWindow();
            }}
          >
            Open in new window
          </ContextMenuItem>
        )}
        <ContextMenuSub>
          <ContextMenuSubTrigger
            disabled={!canEdit}
            className="cursor-pointer rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-hover data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
          >
            Move to
          </ContextMenuSubTrigger>
          <ContextMenuSubContent className="min-w-[10rem] rounded-md border border-border bg-surface p-1 shadow-lg">
            {STATUS_COLS.map((col) => (
              <ContextMenuItem
                key={col.id}
                disabled={col.id === currentStatus}
                className="cursor-pointer rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-hover data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
                onSelect={() => {
                  onClose();
                  onMoveTo(col.id);
                }}
              >
                {col.label}
              </ContextMenuItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>
        <ContextMenuSeparator className="my-1 h-px bg-border" />
        <ContextMenuItem
          disabled={!canArchive}
          className="cursor-pointer rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-hover data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
          onSelect={() => {
            onClose();
            onArchiveToggle();
          }}
        >
          {isArchived ? 'Restore' : 'Archive'}
        </ContextMenuItem>
        <ContextMenuItem
          disabled={!canDelete}
          className="cursor-pointer rounded-sm px-2 py-1.5 text-sm text-danger outline-none hover:bg-hover data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
          onSelect={() => {
            onClose();
            onDelete();
          }}
        >
          Delete
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
